import React from 'react';
import { SaveData, upProfile, SaveApiResMessage } from '@/actions';
import { noScroll, removeNoScroll } from '@/actions/Helper';
import { Input } from '@/components/Input';
import TimePicker from '@/components/TimePicker';
import NumberInput from '@/components/NumberInput';
import IconButton from '@/ctComponents/IconButton';
import Modal from '@/ctComponents/Modal';
import { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import * as yup from 'yup';

const formSchema = yup.object().shape({
  name: yup
    .string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name cannot exceed 50 characters'),
  last_name: yup
    .string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name cannot exceed 50 characters'),
  phone: yup.string().required('Phone number is required'),
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address'),
  address: yup
    .string()
    .required('Office address is required')
    .min(5, 'Address must be at least 5 characters'),
});

export const EditInformation = ({ data, show, setShow }) => {
  // const notify = useNotify();

  const [formData, setFormData] = useState({
    name: '',
    last_name: '',
    company: '',
    phone: '',
    email: '',
    address: '',
    zip: '',
    yard_location: '',
    yard_zip: '',
    yard_start: '',
    yard_end: '',
    yard_scac: '',
    yard_dot: '',
    yard_trucks: 1,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || '',
        last_name: data.last_name || '',
        company: data.company || '',
        phone: data.phone || '',
        email: data.email || '',
        address: data.address || '',
        zip: data.zip || '',
        yard_location: data.yard_location || '',
        yard_zip: data.yard_zip || '',
        yard_start: data.yard_start || '',
        yard_end: data.yard_end || '',
        yard_scac: data.yard_scac || '',
        yard_dot: data.yard_dot || '',
        yard_trucks: data.yard_trucks || 1,
      });
    }
  }, [data]);

  useEffect(() => {
    show ? noScroll() : removeNoScroll();
  }, [show]);

  const validateForm = async () => {
    try {
      await formSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (error) {
      const validationErrors = {};
      error.inner.forEach((err) => {
        validationErrors[err.path] = err.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleTrucksChange = (operation) => {
    setFormData((prev) => {
      const currentCount = parseInt(prev.yard_trucks) || 1;
      let newCount;

      if (operation === 'increment') {
        newCount = currentCount + 1;
      } else if (operation === 'decrement') {
        newCount = Math.max(1, currentCount - 1); // Minimum 1 truck
      }

      return {
        ...prev,
        yard_trucks: newCount,
      };
    });
  };

  const handleTrucksInput = (value) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    const numValue = parseInt(numericValue) || 1;
    const validValue = Math.max(1, numValue); // Ensure minimum 1
    setFormData((prev) => ({
      ...prev,
      yard_trucks: validValue,
    }));
  };

  const handleSave = async () => {
    setIsSubmitting(true);

    try {
      const isValid = await validateForm();

      if (!isValid) {
        // notify('Please fix the errors before submitting', 'error');
        SaveApiResMessage({
          title: 'Error',
          message: 'Please fix the errors before submitting',
          mode: 'error',
        });
        setIsSubmitting(false);
        return;
      }

      let t = {
        ...formData,
        yard_start: formData.yard_start || '',
        yard_end: formData.yard_end || '',
      };
      const res = await upProfile(t);

      SaveData({
        user: {
          ...data,
          name: res.name,
          last_name: res.last_name,
          company: res.company,
          phone: res.phone,
          email: res.email,
          address: res.address,
          zip: res.zip,
          yard_location: res.yard_location,
          yard_zip: res.yard_zip,
          yard_start: res.yard_start,
          yard_end: res.yard_end,
          yard_scac: res.yard_scac,
          yard_dot: res.yard_dot,
          yard_trucks: res.yard_trucks,
        },
      });
      // notify('Profile updated successfully', 'success');
      SaveApiResMessage({
        title: res?.data?.messageHeader,
        message: 'Profile updated successfully',
        mode: 'success',
      });

      setShow(false);
    } catch (error) {
      // notify(error, 'error');
      SaveApiResMessage({
        title: 'Error',
        message: error,
        mode: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (data) {
      setFormData({
        name: data.name || '',
        last_name: data.last_name || '',
        company: data.company || '',
        phone: data.phone || '',
        email: data.email || '',
        address: data.address || '',
        zip: data.zip || '',
        yard_location: data.yard_location || '',
        yard_zip: data.yard_zip || '',
        yard_start: data.yard_start || '',
        yard_end: data.yard_end || '',
        yard_scac: data.yard_scac || '',
        yard_dot: data.yard_dot || '',
        yard_trucks: data.yard_trucks || 1,
      });
    }
    setErrors({});
    setShow(false);
  };

  return (
    <Modal
      noX
      minWidth="unset"
      show={show}
      notClose={true}
      children={
        <div className="w-full overflow-y-auto">
          <div className="min-h-536 pt-4 px-4 space-y-4 md:space-y-0 md:grid grid-cols-1 md:grid-cols-2 gap-4 max-w-screen-sm">
            <Input
              className="text-left"
              label="First Name"
              name="name"
              id="name"
              placeholder="Enter first name"
              error={errors.name}
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              type="text"
            />

            <Input
              className="text-left"
              label="Last Name"
              name="last_name"
              id="last_name"
              placeholder="Enter last name"
              error={errors.last_name}
              value={formData.last_name}
              onChange={(e) => handleChange('last_name', e.target.value)}
              type="text"
            />

            <Input
              className="text-left"
              label="Phone"
              name="phone"
              id="phone"
              placeholder="Enter phone number"
              error={errors.phone}
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              type="tel"
            />

            <Input
              className="text-left"
              label="Email"
              name="email"
              id="email"
              placeholder="Enter email address"
              error={errors.email}
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              type="email"
            />

            <Input
              className="text-left"
              label="Office Address"
              name="address"
              id="address"
              placeholder="Enter office address"
              error={errors.address}
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              type="text"
            />

            <Input
              className="text-left"
              label="Office ZIP Code"
              name="zip"
              id="zip"
              placeholder="Enter office ZIP code"
              error={errors.zip}
              value={formData.zip}
              onChange={(e) => handleChange('zip', e.target.value)}
              type="text"
            />

            <Input
              className="text-left"
              label="Yard Address"
              name="yard_location"
              id="yard_location"
              placeholder="Enter yard address"
              error={errors.yard_location}
              value={formData.yard_location}
              onChange={(e) => handleChange('yard_location', e.target.value)}
              type="text"
            />

            <Input
              className="text-left"
              label="Yard ZIP Code"
              name="yard_zip"
              id="yard_zip"
              placeholder="Enter yard ZIP code"
              error={errors.yard_zip}
              value={formData.yard_zip}
              onChange={(e) => handleChange('yard_zip', e.target.value)}
              type="text"
            />

            <div
              style={{
                padding: '0px 2px',
              }}>
              <span className="text-sm text-primary-text font-bold">
                Yard Hours of Operation
              </span>
              <div
                className="flex items-center gap-2"
                style={{ marginTop: '7px' }}>
                <div className="flex-1">
                  <TimePicker
                    value={formData.yard_start}
                    onChange={(time) => handleChange('yard_start', time)}
                    placeholder="Start Time (e.g., 8:00 AM)"
                    error={errors.yard_start}
                  />
                </div>

                <div className="flex-1">
                  <TimePicker
                    value={formData.yard_end}
                    onChange={(time) => handleChange('yard_end', time)}
                    placeholder="End Time (e.g., 5:00 PM)"
                    error={errors.yard_end}
                  />
                </div>
              </div>
            </div>

            <NumberInput
              label="Number of Trucks"
              value={formData.yard_trucks}
              onIncrement={() => handleTrucksChange('increment')}
              onDecrement={() => handleTrucksChange('decrement')}
              onInputChange={(value) => handleTrucksInput(value)}
              error={errors.yard_trucks}
              min={1}
            />

            <Input
              className="text-left"
              label="SCAC"
              name="yard_scac"
              id="yard_scac"
              placeholder="Enter SCAC"
              error={errors.yard_scac}
              value={formData.yard_scac}
              onChange={(e) => handleChange('yard_scac', e.target.value)}
              type="text"
            />

            <Input
              className="text-left"
              label="DOT"
              name="yard_dot"
              id="yard_dot"
              placeholder="Enter DOT"
              error={errors.yard_dot}
              value={formData.yard_dot}
              onChange={(e) => handleChange('yard_dot', e.target.value)}
              type="text"
            />
          </div>
          <div className="flex col-span-2 justify-end gap-4 mt-4 pt-4 border-t sticky bottom-0 bg-white">
            <IconButton
              onClick={handleCancel}
              text={'Cancel'}
              color={'#131617'}
              size={'normal'}
              style={{
                width: 120,
                height: 40,
              }}
            />
            <IconButton
              onClick={handleSave}
              text={isSubmitting ? 'Saving...' : 'Save Changes'}
              color={'#FFAD00'}
              size={'normal'}
              disabled={isSubmitting}
              style={{
                backgroundColor: '#FFAD00',
                color: '#131617',
                opacity: isSubmitting ? 0.7 : 1,
                width: 127,
                height: 40,
              }}
            />
          </div>
        </div>
      }
      icon={'pen'}
      title="Edit Profile Information"
      setShow={setShow}
    />
  );
};
